import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useMetaTags} from '../../services/useMetaTags';
import {useAuth} from '../../services/use-auth';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import NotFoundPage from '../Errors/NotFound';
import trackPageView from '../../services/trackPageView';
import {parseRouteResponse} from '../../services/route-response';
import '../../assets/styles/surveys/surveyBrowser.scss';

const PER_PAGE = 12;
const EMPTY_BROWSER_STATE = {
    items: [],
    pinnedItems: [],
    pagination: {
        page: 1,
        perPage: PER_PAGE,
        totalItems: 0,
        totalPages: 0,
        hasMore: false,
    },
    filtersMeta: {
        categories: [],
        durationOptions: [],
        rewardOptions: [],
        sortOptions: [],
    },
    activeFilters: {
        search: '',
        sort: 'recommended',
        categories: [],
        duration: [],
        reward: 'all',
        page: 1,
    },
};

function parsePositiveInt(value, fallback = 1) {
    const parsed = Number.parseInt(String(value || ''), 10);

    if (!Number.isFinite(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
}

function parseUniqueIntegerArray(values) {
    const unique = new Set();

    values.forEach((value) => {
        const parsed = Number.parseInt(String(value || ''), 10);
        if (Number.isInteger(parsed) && parsed > 0) {
            unique.add(parsed);
        }
    });

    return Array.from(unique);
}

function parseUniqueStringArray(values, allowedValues = []) {
    const unique = new Set();
    const allowed = new Set(allowedValues);

    values.forEach((value) => {
        const normalized = String(value || '').trim().toLowerCase();
        if (normalized === '') {
            return;
        }

        if (allowed.size > 0 && !allowed.has(normalized)) {
            return;
        }

        unique.add(normalized);
    });

    return Array.from(unique);
}

function getAllQueryValues(params, key) {
    return [
        ...params.getAll(key),
        ...params.getAll(`${key}[]`),
    ];
}

function resolveSortValue(value, isFinished) {
    const normalized = String(value || '').trim().toLowerCase();
    const allowed = isFinished
        ? ['recommended', 'newest', 'popular']
        : ['recommended', 'newest', 'popular', 'ending_soon'];

    if (allowed.includes(normalized)) {
        return normalized;
    }

    return 'recommended';
}

function resolveRewardValue(value) {
    const normalized = String(value || '').trim().toLowerCase();

    if (['all', 'with_points', 'without_reward'].includes(normalized)) {
        return normalized;
    }

    return 'all';
}

function parseQueryState(search, isFinished) {
    const params = new URLSearchParams(search || '');

    return {
        search: params.get('search') || '',
        sort: resolveSortValue(params.get('sort') || 'recommended', isFinished),
        categories: parseUniqueIntegerArray(getAllQueryValues(params, 'categories')),
        duration: parseUniqueStringArray(getAllQueryValues(params, 'duration'), ['short', 'medium', 'long']),
        reward: resolveRewardValue(params.get('reward') || 'all'),
        page: parsePositiveInt(params.get('page') || '1', 1),
    };
}

function buildBrowserQuery(state) {
    const params = new URLSearchParams();

    if ((state.search || '').trim() !== '') {
        params.set('search', state.search.trim());
    }

    if ((state.sort || 'recommended') !== 'recommended') {
        params.set('sort', state.sort);
    }

    (state.categories || []).forEach((value) => {
        params.append('categories[]', String(value));
    });

    (state.duration || []).forEach((value) => {
        params.append('duration[]', value);
    });

    if ((state.reward || 'all') !== 'all') {
        params.set('reward', state.reward);
    }

    if ((state.page || 1) > 1) {
        params.set('page', String(state.page));
    }

    return params.toString();
}

function buildApiQuery(state) {
    const params = new URLSearchParams();
    params.set('page', String(state.page || 1));
    params.set('perPage', String(PER_PAGE));

    if ((state.search || '').trim() !== '') {
        params.set('search', state.search.trim());
    }

    if ((state.sort || 'recommended') !== 'recommended') {
        params.set('sort', state.sort);
    }

    (state.categories || []).forEach((value) => {
        params.append('categories[]', String(value));
    });

    (state.duration || []).forEach((value) => {
        params.append('duration[]', value);
    });

    if ((state.reward || 'all') !== 'all') {
        params.set('reward', state.reward);
    }

    return params.toString();
}

function getPaginationItems(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const items = [1];
    const from = Math.max(2, currentPage - 1);
    const to = Math.min(totalPages - 1, currentPage + 1);

    if (from > 2) {
        items.push('ellipsis-left');
    }

    for (let page = from; page <= to; page += 1) {
        items.push(page);
    }

    if (to < totalPages - 1) {
        items.push('ellipsis-right');
    }

    items.push(totalPages);

    return items;
}

function normalizeImageUrl(value) {
    if (!value) {
        return '/img/fallback-min.png';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function hasAppliedFilters(state) {
    return (state.search || '').trim() !== ''
        || (state.categories || []).length > 0
        || (state.duration || []).length > 0
        || (state.reward || 'all') !== 'all';
}

function SurveyCard({item, isPinned, t}) {
    const statusLabel = t(`surveys.browser.status.${item.status}`, item.status);
    const ctaLabel = item.ctaMode === 'view'
        ? t('surveys.browser.viewCta')
        : t('surveys.browser.takeCta');
    const rewardSummary = item.rewardSummary || t('surveys.browser.noReward');
    const categoryLabel = (item.categoryLabels || [])[0] || t('surveys.browser.allCategories');

    return (
        <article className="survey-card">
            <Link className="survey-card__cover" to={item.url}>
                {isPinned && <span className="survey-card__badge survey-card__badge--top">{t('surveys.top')}</span>}
                {item.isRegistrationRequired && (
                    <span className="survey-card__badge survey-card__badge--lock">
                        {t('surveys.registrationRequired')}
                    </span>
                )}
                <span className="survey-card__badge survey-card__badge--category">{categoryLabel}</span>
                <img src={normalizeImageUrl(item.imageUrl)} alt="" className="survey-card__image" />
                <span className="survey-card__overlay" />
                <span className="survey-card__title">{item.title}</span>
            </Link>

            <div className="survey-card__body">
                <div className="survey-card__meta">
                    <span>{t('surveys.browser.durationMeta', {count: item.estimatedDurationMinutes})}</span>
                    <span className="survey-card__dot">•</span>
                    <span>{t('surveys.browser.questionsMeta', {count: item.questionsCount})}</span>
                    <span className="survey-card__dot">•</span>
                    <span>{rewardSummary}</span>
                </div>

                <div className="survey-card__footer">
                    <span className={`survey-card__status survey-card__status--${item.statusVariant}`}>
                        {statusLabel}
                    </span>
                    <Link className={`survey-card__cta ${item.ctaMode === 'view' ? 'is-ghost' : ''}`} to={item.url}>
                        {ctaLabel}
                    </Link>
                </div>
            </div>
        </article>
    );
}

export default function SurveyBrowser() {
    const {t} = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const auth = useAuth();
    const isFinishedTab = location.pathname.endsWith('/finished');
    const pageTitle = isFinishedTab ? t('surveys.titleFinished') : t('surveys.title');
    const queryState = useMemo(() => parseQueryState(location.search, isFinishedTab), [location.search, isFinishedTab]);
    const [browserData, setBrowserData] = useState(EMPTY_BROWSER_STATE);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [searchValue, setSearchValue] = useState(queryState.search);
    const [draftFilters, setDraftFilters] = useState({
        categories: queryState.categories,
        duration: queryState.duration,
        reward: queryState.reward,
    });
    const [notFound, setNotFound] = useState(false);

    useMetaTags({
        title: pageTitle,
        description: t('app.description'),
        keywords: t('app.keywords'),
    });

    useEffect(() => {
        trackPageView(pageTitle);
    }, [pageTitle]);

    useEffect(() => {
        if (!auth.user && isFinishedTab) {
            history.replace({
                pathname: '/surveys',
                search: location.search,
            });
        }
    }, [auth.user, history, isFinishedTab, location.search]);

    useEffect(() => {
        setSearchValue(queryState.search);
        setDraftFilters({
            categories: queryState.categories,
            duration: queryState.duration,
            reward: queryState.reward,
        });
    }, [queryState]);

    useEffect(() => {
        let cancelled = false;
        const endpoint = isFinishedTab ? '/api/v1/surveys/finished/browser' : '/api/v1/surveys/browser';
        const apiQuery = buildApiQuery(queryState);

        setLoading(true);

        fetch(`${endpoint}?${apiQuery}`)
            .then(async (res) => {
                const result = await res.json().catch(() => null);
                const {kind, redirectUrl} = parseRouteResponse(res, result);

                if (cancelled) {
                    return null;
                }

                if (kind === 'not_found') {
                    setNotFound(true);
                    setLoading(false);
                    return null;
                }

                if ((kind === 'unauthorized' || kind === 'redirect' || kind === 'error') && redirectUrl) {
                    window.location.href = redirectUrl;
                    return null;
                }

                if (kind === 'error') {
                    setLoading(false);
                    return null;
                }

                setNotFound(false);
                return result;
            })
            .then((result) => {
                if (cancelled || !result) {
                    return;
                }

                setBrowserData({
                    ...EMPTY_BROWSER_STATE,
                    ...result,
                    pagination: {
                        ...EMPTY_BROWSER_STATE.pagination,
                        ...(result.pagination || {}),
                    },
                    filtersMeta: {
                        ...EMPTY_BROWSER_STATE.filtersMeta,
                        ...(result.filtersMeta || {}),
                    },
                    activeFilters: {
                        ...EMPTY_BROWSER_STATE.activeFilters,
                        ...(result.activeFilters || {}),
                    },
                });
                setLoading(false);
            })
            .catch(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isFinishedTab, queryState]);

    const applyRouteState = useCallback((nextState, options = {}) => {
        const query = buildBrowserQuery(nextState);

        const nextLocation = {
            pathname: options.pathname || location.pathname,
            search: query ? `?${query}` : '',
        };

        if (options.replace) {
            history.replace(nextLocation);
            return;
        }

        history.push(nextLocation);
    }, [history, location.pathname]);

    const handleSearchSubmit = useCallback((event) => {
        event.preventDefault();
        applyRouteState({
            ...queryState,
            search: searchValue,
            page: 1,
        });
    }, [applyRouteState, queryState, searchValue]);

    const handleSortChange = useCallback((event) => {
        applyRouteState({
            ...queryState,
            sort: resolveSortValue(event.target.value, isFinishedTab),
            page: 1,
        });
    }, [applyRouteState, isFinishedTab, queryState]);

    const handleCategoryChipToggle = useCallback((categoryId) => {
        const nextCategories = queryState.categories.includes(categoryId)
            ? queryState.categories.filter((value) => value !== categoryId)
            : [...queryState.categories, categoryId];

        applyRouteState({
            ...queryState,
            categories: nextCategories,
            page: 1,
        });
    }, [applyRouteState, queryState]);

    const handlePageClick = useCallback((page) => {
        if (page === queryState.page || page < 1) {
            return;
        }

        applyRouteState({
            ...queryState,
            page,
        });

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, [applyRouteState, queryState]);

    const updateDraftCategories = useCallback((categoryId) => {
        setDraftFilters((current) => ({
            ...current,
            categories: current.categories.includes(categoryId)
                ? current.categories.filter((value) => value !== categoryId)
                : [...current.categories, categoryId],
        }));
    }, []);

    const updateDraftDuration = useCallback((durationValue) => {
        setDraftFilters((current) => ({
            ...current,
            duration: current.duration.includes(durationValue)
                ? current.duration.filter((value) => value !== durationValue)
                : [...current.duration, durationValue],
        }));
    }, []);

    const handleApplyFilters = useCallback(() => {
        applyRouteState({
            ...queryState,
            categories: draftFilters.categories,
            duration: draftFilters.duration,
            reward: draftFilters.reward,
            page: 1,
        });
        setDrawerOpen(false);
    }, [applyRouteState, draftFilters, queryState]);

    const handleResetFilters = useCallback(() => {
        const clearedFilters = {
            categories: [],
            duration: [],
            reward: 'all',
        };

        setDraftFilters(clearedFilters);
        applyRouteState({
            ...queryState,
            ...clearedFilters,
            page: 1,
        });
    }, [applyRouteState, queryState]);

    const activeTabHref = useMemo(() => {
        const query = buildBrowserQuery({
            ...queryState,
            page: 1,
        });

        return query ? `/surveys?${query}` : '/surveys';
    }, [queryState]);

    const finishedTabHref = useMemo(() => {
        const query = buildBrowserQuery({
            ...queryState,
            sort: resolveSortValue(queryState.sort, true),
            page: 1,
        });

        return query ? `/surveys/finished?${query}` : '/surveys/finished';
    }, [queryState]);

    const categoryOptions = browserData.filtersMeta.categories || [];
    const durationOptions = browserData.filtersMeta.durationOptions?.length
        ? browserData.filtersMeta.durationOptions
        : [
            {value: 'short'},
            {value: 'medium'},
            {value: 'long'},
        ];
    const rewardOptions = browserData.filtersMeta.rewardOptions?.length
        ? browserData.filtersMeta.rewardOptions
        : [
            {value: 'all'},
            {value: 'with_points'},
            {value: 'without_reward'},
        ];
    const sortOptions = browserData.filtersMeta.sortOptions?.length
        ? browserData.filtersMeta.sortOptions
        : (isFinishedTab
            ? [{value: 'recommended'}, {value: 'newest'}, {value: 'popular'}]
            : [{value: 'recommended'}, {value: 'newest'}, {value: 'popular'}, {value: 'ending_soon'}]);

    const pagination = browserData.pagination || EMPTY_BROWSER_STATE.pagination;
    const paginationItems = getPaginationItems(pagination.page || 1, pagination.totalPages || 0);
    const isFiltered = hasAppliedFilters(queryState);
    const hasResults = (browserData.pinnedItems || []).length > 0 || (browserData.items || []).length > 0;

    if (notFound) {
        return <NotFoundPage />;
    }

    return (
        <section className="survey-browser">
            <div className="container">
                <AdvertiseBlock unitId="surveys1" blockLocation="top" />

                <div className="survey-browser__shell">
                    <header className="survey-browser__head">
                        <div className="survey-browser__title">
                            <h1>{pageTitle}</h1>
                            <p>{t('surveys.browser.subtitle')}</p>
                        </div>

                        <div className="survey-browser__controls">
                            <nav className="survey-browser__tabs" aria-label={t('surveys.browser.tabsLabel')}>
                                <Link className={`survey-browser__tab ${!isFinishedTab ? 'is-active' : ''}`} to={activeTabHref}>
                                    <span>{t('surveys.tabs.active')}</span>
                                    {!isFinishedTab && (
                                        <span className="survey-browser__tab-pill">{pagination.totalItems}</span>
                                    )}
                                </Link>
                                {auth.user ? (
                                    <Link className={`survey-browser__tab ${isFinishedTab ? 'is-active' : ''}`} to={finishedTabHref}>
                                        <span>{t('surveys.tabs.finished')}</span>
                                        {isFinishedTab && (
                                            <span className="survey-browser__tab-pill">{pagination.totalItems}</span>
                                        )}
                                    </Link>
                                ) : (
                                    <span className="survey-browser__tab is-disabled">
                                        <span>{t('surveys.tabs.finished')}</span>
                                    </span>
                                )}
                            </nav>

                            <div className="survey-browser__actions">
                                <label className="survey-browser__sort">
                                    <span>{t('search.filters.sort')}</span>
                                    <select value={queryState.sort} onChange={handleSortChange}>
                                        {sortOptions.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {t(`surveys.browser.sort.${option.value}`, option.label || option.value)}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <button type="button" className="survey-browser__filter-btn" onClick={() => setDrawerOpen(true)}>
                                    {t('search.filters.title')}
                                </button>
                            </div>
                        </div>
                    </header>

                    <header className="survey-bar">
                        <form className="survey-bar__form" onSubmit={handleSearchSubmit} role="search">
                            <label className="survey-bar__search">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M10.5 3a7.5 7.5 0 105.02 13.08l3.2 3.2a1 1 0 001.41-1.41l-3.2-3.2A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" fill="currentColor" />
                                </svg>
                                <input
                                    type="search"
                                    value={searchValue}
                                    onChange={(event) => setSearchValue(event.target.value)}
                                    placeholder={t('surveys.browser.searchPlaceholder')}
                                />
                            </label>
                            <button type="submit" className="survey-bar__submit">
                                {t('search.submit')}
                            </button>
                        </form>
                    </header>

                    <div className="survey-browser__context">
                        <div className="survey-browser__chips">
                            <button
                                type="button"
                                className={`survey-browser__chip ${(queryState.categories || []).length === 0 ? 'is-active' : ''}`}
                                onClick={() => applyRouteState({...queryState, categories: [], page: 1})}
                            >
                                {t('surveys.browser.allCategories')}
                            </button>

                            {categoryOptions.map((category) => (
                                <button
                                    key={category.id}
                                    type="button"
                                    className={`survey-browser__chip ${queryState.categories.includes(category.id) ? 'is-active' : ''}`}
                                    onClick={() => handleCategoryChipToggle(category.id)}
                                >
                                    {category.label}
                                </button>
                            ))}
                        </div>

                        <div className="survey-browser__meta">
                            <span>{t('surveys.browser.found')}</span>
                            <strong>{pagination.totalItems}</strong>
                        </div>
                    </div>

                    <div className="survey-browser__content">
                        {loading ? (
                            <div className="survey-browser__empty">
                                {t('surveys.browser.loading')}
                            </div>
                        ) : hasResults ? (
                            <>
                                <div className="survey-browser__grid">
                                    {(browserData.pinnedItems || []).map((pinnedItem) => (
                                        <SurveyCard
                                            key={`pinned-${pinnedItem.item.id}`}
                                            item={pinnedItem.item}
                                            isPinned={true}
                                            t={t}
                                        />
                                    ))}
                                    {(browserData.items || []).map((item) => (
                                        <SurveyCard key={item.id} item={item} isPinned={false} t={t} />
                                    ))}
                                </div>

                                {pagination.totalPages > 1 && (
                                    <nav className="survey-browser__pagination" aria-label={t('surveys.browser.paginationLabel')}>
                                        <button
                                            type="button"
                                            className="survey-browser__page-btn"
                                            onClick={() => handlePageClick((pagination.page || 1) - 1)}
                                            disabled={(pagination.page || 1) <= 1}
                                        >
                                            {t('search.prev')}
                                        </button>

                                        {paginationItems.map((item) => {
                                            if (typeof item !== 'number') {
                                                return (
                                                    <span key={item} className="survey-browser__page-ellipsis">
                                                        …
                                                    </span>
                                                );
                                            }

                                            return (
                                                <button
                                                    key={item}
                                                    type="button"
                                                    className={`survey-browser__page-btn ${item === pagination.page ? 'is-active' : ''}`}
                                                    onClick={() => handlePageClick(item)}
                                                >
                                                    {item}
                                                </button>
                                            );
                                        })}

                                        <button
                                            type="button"
                                            className="survey-browser__page-btn"
                                            onClick={() => handlePageClick((pagination.page || 1) + 1)}
                                            disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
                                        >
                                            {t('search.next')}
                                        </button>
                                    </nav>
                                )}
                            </>
                        ) : (
                            <div className="survey-browser__empty">
                                {isFinishedTab
                                    ? t(isFiltered ? 'surveys.browser.noFinishedFor' : 'surveys.browser.noFinished')
                                    : t(isFiltered ? 'surveys.browser.noResultsFor' : 'surveys.browser.noResults')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div
                className={`survey-browser__backdrop ${drawerOpen ? 'is-open' : ''}`}
                onClick={() => setDrawerOpen(false)}
                aria-hidden={!drawerOpen}
            />

            <aside className={`survey-drawer ${drawerOpen ? 'is-open' : ''}`} aria-hidden={!drawerOpen}>
                <div className="survey-drawer__head">
                    <strong>{t('search.filters.title')}</strong>
                    <button type="button" className="survey-drawer__close" onClick={() => setDrawerOpen(false)}>
                        {t('surveys.browser.close')}
                    </button>
                </div>

                <div className="survey-drawer__body">
                    <section className="survey-drawer__group">
                        <h2>{t('surveys.browser.filters.categories')}</h2>
                        {categoryOptions.map((category) => (
                            <label key={category.id} className="survey-drawer__check">
                                <input
                                    type="checkbox"
                                    checked={draftFilters.categories.includes(category.id)}
                                    onChange={() => updateDraftCategories(category.id)}
                                />
                                <span>{category.label}</span>
                            </label>
                        ))}
                    </section>

                    <section className="survey-drawer__group">
                        <h2>{t('surveys.browser.filters.duration.title')}</h2>
                        {durationOptions.map((option) => (
                            <label key={option.value} className="survey-drawer__check">
                                <input
                                    type="checkbox"
                                    checked={draftFilters.duration.includes(option.value)}
                                    onChange={() => updateDraftDuration(option.value)}
                                />
                                <span>{t(`surveys.browser.filters.duration.${option.value}`, option.label || option.value)}</span>
                            </label>
                        ))}
                    </section>

                    <section className="survey-drawer__group">
                        <h2>{t('surveys.browser.filters.reward.title')}</h2>
                        <label className="survey-drawer__select">
                            <select
                                value={draftFilters.reward}
                                onChange={(event) => setDraftFilters((current) => ({
                                    ...current,
                                    reward: resolveRewardValue(event.target.value),
                                }))}
                            >
                                {rewardOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {t(`surveys.browser.filters.reward.${option.value}`, option.label || option.value)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </section>
                </div>

                <div className="survey-drawer__actions">
                    <button type="button" className="survey-drawer__apply" onClick={handleApplyFilters}>
                        {t('search.filters.apply')}
                    </button>
                    <button type="button" className="survey-drawer__reset" onClick={handleResetFilters}>
                        {t('search.filters.reset')}
                    </button>
                </div>
            </aside>
        </section>
    );
}
